import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { slugify } from './slugify';

// Tags/attributes allowed in stored + rendered article HTML.
// Keep this in sync with the DOMPurify config in src/pages/Article.js.
export const ARTICLE_ALLOWED_TAGS = [
  'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 'a',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ol', 'ul', 'li', 'blockquote',
  'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'pre', 'code',
];
export const ARTICLE_ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'title'];

marked.setOptions({ gfm: true, breaks: false });

/**
 * Split a raw markdown string into YAML-ish frontmatter and body.
 * Supports a leading block delimited by --- ... --- with simple key: value lines.
 */
function splitFrontmatter(raw) {
  const fmMatch = raw.match(/^\uFEFF?---\s*\n([\s\S]*?)\n---\s*\n?/);
  if (!fmMatch) return { frontmatter: {}, body: raw.replace(/^\uFEFF/, '') };

  const frontmatter = {};
  fmMatch[1].split('\n').forEach((line) => {
    const m = line.match(/^\s*([A-Za-z0-9_-]+)\s*:\s*(.*)\s*$/);
    if (m) {
      let value = m[2].trim();
      value = value.replace(/^["']|["']$/g, '');
      frontmatter[m[1].toLowerCase()] = value;
    }
  });
  return { frontmatter, body: raw.slice(fmMatch[0].length) };
}

/** Turn a filename into a clean slug, e.g. "Life-with-God-Lesson-01.md" -> "life-with-god-lesson-01". */
export function slugFromFilename(filename) {
  const base = (filename || '').replace(/\.(md|markdown)$/i, '');
  return slugify(base);
}

/** Pull a lesson number out of a filename, e.g. "...Lesson-01.md" -> 1. Returns null if none. */
export function lessonNumberFromFilename(filename) {
  const m = (filename || '').match(/lesson[\s_-]*0*(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
}

function firstMatch(body, regex) {
  const m = body.match(regex);
  return m ? m[1].trim() : '';
}

/** Remove the leading H1, H2, italic subtitle line, and any leading horizontal rules from the body. */
function stripLeadingHeaderBlock(body) {
  const lines = body.split('\n');
  let removedH1 = false;
  let removedH2 = false;
  let removedSubtitle = false;
  let i = 0;

  for (; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line === '') continue;
    if (!removedH1 && /^#\s+/.test(line)) { lines[i] = ''; removedH1 = true; continue; }
    if (!removedH2 && /^##\s+/.test(line)) { lines[i] = ''; removedH2 = true; continue; }
    if (!removedSubtitle && /^[*_][^*_].*[*_]$/.test(line)) { lines[i] = ''; removedSubtitle = true; continue; }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) { lines[i] = ''; continue; }
    // Reached real content (e.g. ### heading or a paragraph) -> stop trimming.
    break;
  }

  return lines.join('\n').replace(/^\s+/, '');
}

/** Plain-text excerpt for a description, from the first real paragraph of the body. */
function deriveDescription(body, maxLen = 160) {
  const stripped = stripLeadingHeaderBlock(body);
  const blocks = stripped.split(/\n\s*\n/);
  const paragraph = blocks.find((b) => {
    const t = b.trim();
    if (!t) return false;
    if (/^#{1,6}\s/.test(t)) return false; // heading
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) return false; // hr
    if (/^>/.test(t)) return false; // blockquote
    return true;
  }) || '';

  const text = paragraph
    .replace(/\s+/g, ' ')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .trim();

  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim()}...`;
}

/** Strip a leading "Lesson One — " / "Lesson 1 - " prefix from an H2 to get the bare lesson name. */
function stripLessonPrefix(h2) {
  return h2.replace(/^lesson\s+[\w-]+\s*[—–-]\s*/i, '').trim();
}

/**
 * Compose the article title in the format:
 *   "{lessonName} - Lesson {N} of {total} - {seriesTitle}"
 * Falls back gracefully when pieces are missing.
 */
export function composeLessonTitle({ lessonName, lessonNumber, total, seriesTitle }) {
  const parts = [];
  if (lessonName) parts.push(lessonName);
  if (lessonNumber != null) {
    parts.push(total ? `Lesson ${lessonNumber} of ${total}` : `Lesson ${lessonNumber}`);
  }
  if (seriesTitle) parts.push(seriesTitle);
  return parts.join(' - ');
}

/**
 * Parse a single markdown file into the pieces needed to build an article/resource.
 * Note: the final `title` should be composed by the caller once `total` is known.
 */
export function parseMarkdownFile(filename, raw) {
  const { frontmatter, body } = splitFrontmatter(raw);

  const seriesTitle = frontmatter.series || firstMatch(body, /^#\s+(.+)$/m);
  const h2 = firstMatch(body, /^##\s+(.+)$/m);
  const lessonName = frontmatter.title || (h2 ? stripLessonPrefix(h2) : '') || slugFromFilename(filename).replace(/-/g, ' ');
  const subtitle = frontmatter.subtitle || firstMatch(body, /^\s*[*_]([^*_][^*\n]*)[*_]\s*$/m);
  const lessonNumber = lessonNumberFromFilename(filename);

  const contentMarkdown = stripLeadingHeaderBlock(body);
  const rawHtml = marked.parse(contentMarkdown);
  const html = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ARTICLE_ALLOWED_TAGS,
    ALLOWED_ATTR: ARTICLE_ALLOWED_ATTR,
  });

  const description = frontmatter.description || deriveDescription(body);

  const wordCount = contentMarkdown
    .replace(/[#>*_`-]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 0).length;

  return {
    filename,
    slug: slugFromFilename(filename),
    seriesTitle,
    lessonName,
    lessonNumber,
    subtitle,
    description,
    html,
    wordCount,
    author: frontmatter.author || '',
    authorWebsite: frontmatter.authorwebsite || frontmatter.website || '',
  };
}
